import { FunctionComponent, useState } from "react"
import { Column } from "./table"
import TableFilterDropdown from "./table-filter-dropdown"
import { FilterIcon, SortIconPlaceholder } from "./table-shared"

export type TableHeadProps = {
  columns: Column[]
  allSelected: boolean
  onSelectAll: () => void
  showCheckboxColumn: boolean
  onSort: (columnName: string) => void
  sortColumn: string | null
  sortDirection: "asc" | "desc" | "off"
  filterConfig?: { [key: string]: FilterConfig }
  filters?: { [key: string]: string[] }
  handleFilterChange?: (columnName: string, value: string) => void
}

export type FilterConfig = {
  values: string[] // List of values to filter by
}

const TableHead: FunctionComponent<TableHeadProps> = ({
  columns,
  allSelected,
  onSelectAll,
  showCheckboxColumn,
  onSort,
  sortColumn,
  sortDirection,
  filterConfig,
  filters,
  handleFilterChange,
}) => {
  const [showFilterDropdown, setShowFilterDropdown] = useState(false)
  const [showFilterColumn, setShowFilterColumn] = useState<string | null>(null)
  return (
    <thead className="bg-blue-50">
      <tr>
        {showCheckboxColumn && (
          <th className="px-4 py-3 first:pl-8 align-top border-b last:border-r-0 border-r border-solid border-blue-100 text-left text-black select-none">
            <input
              type="checkbox"
              checked={allSelected}
              onChange={onSelectAll}
              className="w-4 h-4 shrink-0 text-blue-600 bg-white rounded-sm focus:ring-blue-500 cursor-pointer checked:bg-blue-500"
            />
          </th>
        )}
        {columns.map((header, index) => (
          <th
            className={`px-4 py-3 first:pl-8 align-top border-b last:border-r-0 border-r border-solid border-blue-100 cursor-pointer select-none ${header.styles}`}
            key={index}
            onClick={() => onSort(header.name)}
            onMouseEnter={(e) => {
              const icon = e.currentTarget.querySelector("#sortIconPlaceholder")
              if (icon) icon.classList.remove("invisible")
            }}
            onMouseLeave={(e) => {
              const icon = e.currentTarget.querySelector("#sortIconPlaceholder")
              if (icon) icon.classList.add("invisible")
            }}>
            <div className="h-4 flex items-center">
              <p className="text-sm text-blue-900 font-semibold">{header.label}</p>

              {/* Filtering for non-sortable columns */}
              {filterConfig?.[header.name] && (
                <div className="relative">
                  <div
                    onClick={(e) => {
                      e.stopPropagation()
                      setShowFilterDropdown((prev) => !prev)
                      setShowFilterColumn(header.name)
                    }}>
                    <FilterIcon
                      fillColor={
                        filters?.[header.name] &&
                        filters[header.name].length > 0
                          ? "black"
                          : "none"
                      }
                    />
                  </div>
                  {showFilterDropdown && showFilterColumn === header.name && (
                    <TableFilterDropdown
                      onSelect={(value) => {
                        handleFilterChange?.(header.name, value)
                      }}
                      onClose={() => {
                        setShowFilterDropdown(false)
                        handleFilterChange?.(header.name, "closed")
                      }}
                      options={filterConfig[header.name]?.values}
                      selectedValues={filters?.[header.name] || []}
                    />
                  )}
                </div>
              )}
              {/* Sorting for non-filterable columns */}
              {!["activated", "simulation", "groupName", "roleName"].includes(
                header.name,
              ) && (
                <span className="w-4 h-4">
                  {sortColumn === header.name ? (
                    sortDirection === "asc" ? (
                      <svg
                        className="w-4 h-4 text-blue-500"
                        fill="currentColor"
                        viewBox="0 0 20 20">
                        <path d="M10 14l-5-5h10l-5 5z" />
                      </svg>
                    ) : sortDirection === "desc" ? (
                      <svg
                        className="w-4 h-4 text-blue-500"
                        fill="currentColor"
                        viewBox="0 0 20 20">
                        <path d="M10 6l5 5H5l5-5z" />
                      </svg>
                    ) : (
                      <SortIconPlaceholder />
                    )
                  ) : (
                    <SortIconPlaceholder />
                  )}
                </span>
              )}
            </div>
          </th>
        ))}
      </tr>
    </thead>
  )
}

export default TableHead
